import clsx from 'clsx'
import { type ButtonHTMLAttributes, type ReactNode, forwardRef } from 'react'

export type ButtonVariant = 'primary' | 'secondary' | 'ghost' | 'danger'
export type ButtonSize = 'sm' | 'md' | 'lg'
export type ButtonShape = 'rounded' | 'pill'

export interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: ButtonVariant
  size?: ButtonSize
  /** `pill` gives an Apple-style fully-rounded CTA; `rounded` uses the default radius. */
  shape?: ButtonShape
  leftIcon?: ReactNode
  rightIcon?: ReactNode
  loading?: boolean
}

const variantClass: Record<ButtonVariant, string> = {
  // Apple blue primary CTA — solid fill, soft hover lift in color only.
  primary:
    'bg-[var(--accent)] text-[var(--accent-text)] hover:bg-[var(--accent-2)] active:bg-[var(--accent-2)] disabled:opacity-50',
  // Apple "secondary" (filled gray) — soft tinted background, ink text.
  secondary: 'bg-[var(--bg-2)] text-[var(--text)] hover:bg-[var(--bg-3)] disabled:opacity-50',
  // Apple "ghost" — transparent with hairline border.
  ghost:
    'bg-transparent text-[var(--text)] hover:bg-[var(--bg-2)] border border-[var(--border)] disabled:opacity-50',
  danger: 'bg-[var(--red)] text-white hover:brightness-110 disabled:opacity-50',
}

const sizeClass: Record<ButtonSize, string> = {
  sm: 'h-8 px-3 text-[13px]',
  md: 'h-10 px-4 text-[14px]',
  lg: 'h-12 px-6 text-[15px]',
}

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(function Button(
  {
    variant = 'primary',
    size = 'md',
    shape,
    leftIcon,
    rightIcon,
    loading = false,
    disabled,
    className,
    children,
    type = 'button',
    ...rest
  },
  ref,
) {
  const isDisabled = disabled || loading
  // Default shape: pill for primary CTAs (Apple convention), rounded for the rest.
  const resolvedShape: ButtonShape = shape ?? (variant === 'primary' ? 'pill' : 'rounded')
  const radius = resolvedShape === 'pill' ? 'rounded-full' : 'rounded-[var(--radius)]'
  return (
    <button
      ref={ref}
      type={type}
      disabled={isDisabled}
      aria-busy={loading || undefined}
      data-loading={loading || undefined}
      className={clsx(
        'inline-flex items-center justify-center gap-2 font-medium',
        radius,
        'transition-colors duration-200 ease-[cubic-bezier(0.42,0,0.58,1)]',
        'focus-visible:shadow-[var(--focus-ring)] focus-visible:outline-none',
        'disabled:cursor-not-allowed select-none',
        variantClass[variant],
        sizeClass[size],
        className,
      )}
      {...rest}
    >
      {loading ? (
        <span
          aria-hidden="true"
          className="inline-block h-3 w-3 animate-spin rounded-full border-2 border-current border-t-transparent"
        />
      ) : leftIcon ? (
        <span aria-hidden="true" className="inline-flex">
          {leftIcon}
        </span>
      ) : null}
      {children}
      {rightIcon ? (
        <span aria-hidden="true" className="inline-flex">
          {rightIcon}
        </span>
      ) : null}
    </button>
  )
})
